import React, { useEffect, useState } from 'react';
import { Box, Breadcrumbs, Link, Typography } from '@mui/material';
import GenericButton, { GenericButtonTypeEnum } from '../../../../../components/GenericButton';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ActionMode,
  EventFormStep,
  EventTitleEnum,
  EVENT_TITLE_TRANSLATIONS,
  GeneralStatusEnum,
  KindergartenDetailsTabs
} from '../../../../../common/enums/common.enum';
import { getLocationsWIthGroups } from '../../../../../api/kindergarten.api';
import { LocationDto } from '../../../../../common/interfaces/kindergarten.interface';
import { Form, Formik } from 'formik';
import GenericLocationGroupsSelector from '../../../../../components/GenericLocationGroupSelector/GenericLocationGroupSelector';
import {
  createMultiEventConfiguration,
  getAvailableIconsBasedOnParentEvent,
  getBasicParentEventSetup,
  getMainEvents
} from '../../../../../api/group.api';
import { createNotification } from '../../../../../utilities/helper/notification.helper';
import { MESSAGE_TYPE } from '../../../../../common/constants/common.constants';
import EventChildConfiguration from '../../../../../components/EventChildConfiguration/EventChildConfiguration';
import { ReactComponent as WarningIcon } from '../../../../../assets/warning-icon.svg';
import EventDetails from '../EventDetails/EventDetails';
import { BasicEventConfig, CreateMultiGroupEventsDto, ParentEventsOptionDto } from '../../../../../common/interfaces/event.interface';
import ParentEventSelector from '../../../../../components/EventSelector/ParentEventSelector';
import EventTabs from '../../../../../components/EventTabs';
import StepperCircles from '../../../../../components/StepperCircles/StepperCircles';
import { getEnumKeyByValue } from '../../../../../utilities/helper/enum.helper';
import { pickTranslations } from '../../../../../utilities/helper/language.helper';
import { getStepSchemas } from './CreateConfiguration.validation';
import { useTranslation } from 'react-i18next';
// loaders read the i18n instance, not the hook's t: capturing t would make them
// effect dependencies, and re-running a fetch on a language switch buys nothing
import i18n from '../../../../../i18n';

import { palette } from '../../../../../theme';
export interface FormValues {
  selectedGroups: string[];
  selectedEvents: EventTitleEnum[];
  eventConfigs: Record<EventTitleEnum, BasicEventConfig>;
}

const CreateConfiguration = () => {
  const { t } = useTranslation();
  const { kindergartenId, kindergartenName } = useParams<{
    kindergartenId: string;
    kindergartenName: string;
  }>();
  const navigate = useNavigate();
  const locationState = useLocation().state as { preSelectedGroupId?: string } | undefined;
  const singleGroupMode = !!locationState?.preSelectedGroupId;

  // TODO:add loader inside of formik
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState<EventFormStep>(EventFormStep.GroupAndEventSelectStep);
  const [locations, setLocations] = useState<LocationDto[]>([]);
  const [activeTab, setActiveTab] = useState<EventTitleEnum | null>(null);
  const [parentEvents, setParentEvents] = useState<ParentEventsOptionDto[]>([]);
  const [eventIcons, setEventIcons] = useState<string[]>([]);

  const initialSelectedGroups = locationState?.preSelectedGroupId ? [locationState.preSelectedGroupId] : [];
  const stepSchemas = getStepSchemas(singleGroupMode, t);

  useEffect(() => {
    fetchLocations();
  }, [kindergartenId]);

  useEffect(() => {
    if (activeStep === EventFormStep.GroupAndEventSelectStep && kindergartenId) {
      getParentEvents(kindergartenId);
    }
  }, [activeStep, kindergartenId]);

  useEffect(() => {
    if (activeTab && kindergartenId) {
      fetchOptionIconForEvent(activeTab, kindergartenId);
    }
  }, [activeTab, kindergartenId]);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      if (kindergartenId) {
        const response = await getLocationsWIthGroups(kindergartenId, GeneralStatusEnum.ACTIVE);
        const filteredLocations = (response.results ?? [])
          .map(location => ({
            ...location,
            groups: location.groups?.filter(group => !group.hasEvents) || []
          }))
          .filter(location => (location.groups?.length ?? 0) > 0);

        setLocations(filteredLocations);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadBasicParentEventsSetup = async (
    selectedEvents: EventTitleEnum[],
    kindergartenId: string,
    setFieldValue: (field: string, value: any) => void,
    currentConfigs: Record<EventTitleEnum, BasicEventConfig>
  ) => {
    try {
      Object.keys(currentConfigs).forEach(ev => {
        if (!selectedEvents.includes(ev as EventTitleEnum)) {
          setFieldValue(`eventConfig.${ev}`, undefined);
        }
      });

      const eventsToFetch = selectedEvents.filter(ev => {
        const conf = currentConfigs[ev];
        return !conf;
      });

      if (eventsToFetch.length === 0) return;

      setLoading(true);

      const response = await getBasicParentEventSetup(kindergartenId, selectedEvents);
      if (!response.results) return;

      response.results.forEach((config: BasicEventConfig) => {
        const eventKey = config.title as EventTitleEnum;
        if (eventsToFetch.includes(eventKey)) {
          setFieldValue(`eventConfigs.${eventKey}`, config);
        }
      });
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, t('events.setupLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const getParentEvents = async (kindergartenId: string) => {
    try {
      setLoading(true);
      const result = await getMainEvents(kindergartenId);
      if (result?.results) {
        setParentEvents(result.results);
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, i18n.t('events.mainEventsLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchOptionIconForEvent = async (event: EventTitleEnum, kindergartenId: string) => {
    try {
      setLoading(true);
      const result = await getAvailableIconsBasedOnParentEvent(kindergartenId, event);
      setEventIcons(result?.results ?? []);
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, i18n.t('events.mainEventsLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleNextStep = async (values: FormValues, setFieldValue: (field: string, value: any) => void) => {
    if (!values) return;

    switch (activeStep) {
      case EventFormStep.GroupAndEventSelectStep:
        if (values.selectedEvents.length > 0 && kindergartenId) {
          await loadBasicParentEventsSetup(values.selectedEvents, kindergartenId, setFieldValue, values.eventConfigs);
          setActiveTab(values.selectedEvents[0]);
          setActiveStep(EventFormStep.EventSetupStep);
        }
        break;

      case EventFormStep.EventSetupStep:
        setActiveTab(values.selectedEvents[0]);
        setActiveStep(EventFormStep.ReviewStep);
        break;

      case EventFormStep.ReviewStep:
        handleFinalSubmit(values);
        break;

      default:
        break;
    }
  };

  const handleFinalSubmit = async (values: FormValues) => {
    if (!values.selectedGroups || values.selectedGroups.length === 0) return;
    if (!values.selectedEvents || values.selectedEvents.length === 0) return;

    try {
      const payload: CreateMultiGroupEventsDto = {
        groupIds: values.selectedGroups,
        groupEvents: Object.entries(values.eventConfigs).map(([mainEventKey, eventConfig]) => {
          const enumKey = getEnumKeyByValue(EventTitleEnum, mainEventKey);
          if (!enumKey) {
            throw new Error(`Enum key not found for value: ${mainEventKey}`);
          }
          let childEvents = eventConfig.childEvents ?? [];
          if (childEvents.length === 0) {
            const defaultChild = {
              title: EVENT_TITLE_TRANSLATIONS[enumKey as EventTitleEnum],
              ...(mainEventKey === EventTitleEnum.TOILET && eventIcons?.length > 0
                ? { icon: `${process.env.REACT_APP_EVENTS_FOLDER_NAME}/default-icons/toilet/poop.svg` }
                : {})
            };
            childEvents = [defaultChild];
          }

          return {
            title: eventConfig.title,
            isDailyRequired: eventConfig.isDailyRequired,
            childEvents: childEvents.map(child => ({
              ...child,
              title: pickTranslations(child.title)
            })),
            options: eventConfig.options.map(opt => ({
              title: pickTranslations(opt.title),
              value: opt.value,
              icon: opt.icon
            }))
          };
        })
      };
      if (kindergartenId) {
        await createMultiEventConfiguration(payload, kindergartenId);
      }
      createNotification(MESSAGE_TYPE.SUCCESS, t('events.created'));
      navigate(`/${kindergartenId}/details?tab=${KindergartenDetailsTabs.EventConfiguration}`);
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, t('events.createFailed'));
    }
  };

  const goToPreviousStep = () => {
    setActiveStep(prev => {
      switch (prev) {
        case EventFormStep.EventSetupStep:
          return EventFormStep.GroupAndEventSelectStep;
        case EventFormStep.ReviewStep:
          return EventFormStep.EventSetupStep;
        default:
          return prev;
      }
    });
  };

  return (
    <Box p={7}>
      <Breadcrumbs aria-label={t('common.breadcrumb')} sx={{ mb: 3 }}>
        <Link color="text.secondary" underline="hover" onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
          {t('nav.kindergartens')}
        </Link>
        {kindergartenName && (
          <Link
            color="text.secondary"
            underline="hover"
            onClick={() => navigate(`/${kindergartenId}/details?tab=${KindergartenDetailsTabs.EventConfiguration}`)}
            sx={{ cursor: 'pointer' }}
          >
            {kindergartenName}
          </Link>
        )}
        <Typography color="text.primary">{t('events.createConfiguration')}</Typography>
      </Breadcrumbs>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb="44px">
        <Typography variant="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {t('events.createConfiguration')}
        </Typography>
      </Box>
      <StepperCircles activeStep={activeStep} />

      <Formik<FormValues>
        initialValues={{
          selectedGroups: singleGroupMode ? initialSelectedGroups : [],
          selectedEvents: [],
          eventConfigs: {} as Record<EventTitleEnum, BasicEventConfig>
        }}
        validateOnBlur={activeStep !== EventFormStep.GroupAndEventSelectStep}
        validateOnChange={activeStep === EventFormStep.GroupAndEventSelectStep}
        validationSchema={stepSchemas[activeStep]}
        onSubmit={(values, formikHelpers) => handleNextStep(values, formikHelpers.setFieldValue)}
      >
        {({ values }) => (
          <Form>
            {activeStep === EventFormStep.GroupAndEventSelectStep && (
              <Box>
                {!singleGroupMode && (
                  <Box sx={{ mb: 5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="caption">{t('events.chooseWhereToApply')}</Typography>
                    <GenericLocationGroupsSelector locations={locations} useFormik={true} />
                  </Box>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="caption">{t('events.selectMainEvents')}</Typography>
                  <ParentEventSelector events={parentEvents} />
                </Box>
              </Box>
            )}
            {activeStep === EventFormStep.EventSetupStep && activeTab && (
              <Box>
                <EventTabs activeTab={activeTab} setActiveTab={setActiveTab} parentEvents={parentEvents} />
                <Box py={3}>
                  <Box display="inline-flex" alignItems="center" bgcolor={palette.tint.info} px={2} py={1} pr={3} borderRadius={1}>
                    <WarningIcon />
                    <Typography variant="caption" px={2} py={1} bgcolor={palette.tint.info}>
                      {t('events.atLeastTwoOptionsHint')}
                    </Typography>
                  </Box>
                </Box>
                {values.eventConfigs[activeTab] && <EventChildConfiguration eventName={activeTab} eventIcons={eventIcons} />}
              </Box>
            )}
            {activeStep === EventFormStep.ReviewStep && activeTab && (
              <Box py={3}>
                <Box mb={3}>
                  <GenericLocationGroupsSelector
                    locations={locations.map(location => ({
                      ...location,
                      groups: location.groups?.filter(group => values.selectedGroups.includes(group.id!)) || []
                    }))}
                    selectedGroups={values.selectedGroups}
                    disabled={true}
                  />
                </Box>
                <EventTabs activeTab={activeTab} setActiveTab={setActiveTab} parentEvents={parentEvents} />
                {values.eventConfigs[activeTab] && (
                  <EventDetails eventName={activeTab} mode={ActionMode.DETAILS} eventDetailsMap={values.eventConfigs} />
                )}
              </Box>
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: activeStep > EventFormStep.GroupAndEventSelectStep ? 'space-between' : 'flex-end',
                alignItems: 'center',
                mt: 3
              }}
            >
              {activeStep > EventFormStep.GroupAndEventSelectStep && (
                <GenericButton
                  buttonType={GenericButtonTypeEnum.Secondary}
                  variant="outlined"
                  label={t('common.back')}
                  onClick={goToPreviousStep}
                />
              )}
              <GenericButton
                buttonType={GenericButtonTypeEnum.Primary}
                variant="contained"
                label={activeStep === EventFormStep.ReviewStep ? t('common.finish') : t('common.next')}
                type="submit"
              />
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateConfiguration;
